import { Fragment, useEffect, useRef, useState } from 'react';
import { MotionConfig } from 'framer-motion';
import type { WorkOrderDto } from '@signalvote/shared';
import { ApiError } from '@signalvote/api-client';
import { useCreateVoterDataOrder, useRequoteVoterDataOrder } from '@/hooks/queries';
import { Button } from '@/components/ui/button';
import { VoterDataExplore } from './VoterDataExplore';
import { VoterDataSuggestions } from './VoterDataSuggestions';
import { VoterEstimate } from './VoterCount';
import { VoterDataListSummary, VoterDataPurposePicker } from './VoterDataPurpose';
import { VoterListSentence, joinerOf, useVoterList } from './VoterListSentence';
import { CheckoutStep, VoterDataConfirm, useVoterDataCheckout } from './VoterDataConfirm';
import { highestOf } from './voterEstimate';
import type { Estimate } from './voterEstimate';
import { formatDollars, nounFor } from './voterPurpose';
import { orderingUnavailable } from './voterDataOrder';
import { useScrolledAway } from './voterMotion';
import { phraseOf } from './voterSegment';
import { fitsInUrl, readTabDraft, readTabList, restoreList, savedListOf, writeTabDraft, writeTabList } from './voterSavedList';
import type { PlacedDraft, SavedList } from './voterSavedList';
import { useCampaignStore } from '@/stores';

/**
 * The Voter data builder. It starts by asking what the list is for (Doors, Phones, Mail or
 * Turnout), which sets any starting rules and the columns delivered; the
 * purpose can be changed later without losing the customer's rules. Then, sentence first, the customer reads who they're
 * buying as a sentence ("Voters in House District 28 who are not Republican and are women"), the
 * largest thing on the page, and narrows it with one filter-state model: a field with no rule is
 * Everyone, every box checked; unchecking narrows it to "Only N of M"; yes/no fields are
 * Any · Yes · No. Each highlighted phrase opens its field in a popover that grows from it, and the
 * sentence's last blank finds fields and values to add, or takes a plain-English request that
 * applies at once, says how it was read and can be undone.
 *
 * It sits outside the paywall overlay (ADR-090). The last counts stay on screen, as estimates, while
 * the next load, and while L2 is busy: a rate limit is a wait, never an error.
 *
 * The list being built is restored from `saved` (the URL) or else this tab's session storage, and
 * every change is saved to both, so a reload or Back never loses it. A list belongs to one
 * campaign, and switching campaign happens without a reload, so the builder starts over for each
 * campaign it's shown.
 */
export function VoterDataBuildScreen(props: { saved?: SavedList; onSave?: (list: SavedList | undefined) => void } = {}) {
  const campaignId = useCampaignStore((s) => s.selectedCampaign?.id ?? null);
  return <VoterDataBuilder key={campaignId} campaignId={campaignId} {...props} />;
}

function VoterDataBuilder({
  campaignId,
  saved,
  onSave,
}: {
  campaignId: string | null;
  saved?: SavedList;
  onSave?: (list: SavedList | undefined) => void;
}) {
  const v = useVoterList({
    initial: (catalog, newRuleId) =>
      campaignId
        ? (restoreList(saved, campaignId, catalog, newRuleId) ?? restoreList(readTabList(campaignId), campaignId, catalog, newRuleId))
        : null,
  });
  const { ready, state, areas, rules, purpose, defaultRuleIds, segment, counts, list, total } = v;
  const latestOnSave = useRef(onSave);
  useEffect(() => {
    latestOnSave.current = onSave;
  }, [onSave]);
  useEffect(() => {
    if (!ready || !purpose || !campaignId) return;
    const saving = savedListOf(campaignId, { state, areas, rules, purpose, defaultRuleIds });
    writeTabList(saving);
    latestOnSave.current?.(fitsInUrl(saving) ? saving : undefined);
  }, [ready, campaignId, state, areas, rules, purpose, defaultRuleIds]);

  // The sentence mounts only once a purpose is chosen, so it's held in state for the observer to find.
  const [sentence, setSentence] = useState<HTMLHeadingElement | null>(null);
  const sentenceAway = useScrolledAway(sentence);
  const listed = segment !== null && counts.data !== undefined;

  // Ordering sends the Segment and the sentence the customer is reading. The server quotes the
  // Segment afresh, so no count or price goes with it, and an estimate on screen is fine: the
  // confirm step shows the exact price before anything is charged, then takes the card. The tab
  // holds the one Draft this builder placed: every later order re-quotes it with the list as it now
  // stands, until it is checked out or gone, and only then is another placed. One key per new list,
  // so a retry of a failed order finds the Draft that order may already have placed.
  const { mutateAsync: createOrder } = useCreateVoterDataOrder();
  const { mutateAsync: requoteOrder } = useRequoteVoterDataOrder();
  const checkout = useVoterDataCheckout();
  const [orderedAt, setOrderedAt] = useState<Estimate | null>(null);
  const placed = useRef<PlacedDraft | undefined | null>(null);
  if (placed.current === null) placed.current = campaignId ? readTabDraft(campaignId) : undefined;
  const orderKey = useRef<{ segment: string; key: string } | null>(null);
  // Every quote moves the Draft's token, the re-read after a changed price included, so the Draft is
  // taken from the dialog each time it shows one.
  const quoted = checkout.checkout?.step === CheckoutStep.Confirming ? checkout.checkout.draft : null;
  useEffect(() => {
    if (!quoted) return;
    placed.current = { id: quoted.id, concurrencyVersion: quoted.concurrencyVersion };
    orderKey.current = null;
    if (campaignId) writeTabDraft(campaignId, placed.current);
  }, [quoted, campaignId]);
  const ordering = v.catalog.data?.ordering;
  const minimumOrderCents = v.catalog.data?.minimumOrderCents ?? 0;
  const belowMinimum = list !== null && highestOf(list.priceCents) < minimumOrderCents;
  const canOrder = segment !== null && list !== null && list.records.value > 0 && !counts.error && ordering?.available === true && !belowMinimum && v.unanswered === null;
  const order = () => {
    if (!canOrder) return;
    const sentence = v.sentenceText;
    const place = async (): Promise<WorkOrderDto> => {
      if (placed.current) {
        try {
          return await requoteOrder({ id: placed.current.id, body: { segment, sentence, expectedConcurrencyVersion: placed.current.concurrencyVersion } });
        } catch (error) {
          // Checked out, cancelled or lost since: no longer this tab's Draft to change.
          if (!(error instanceof ApiError && (error.status === 404 || error.status === 409))) throw error;
          placed.current = undefined;
          if (campaignId) writeTabDraft(campaignId, undefined);
        }
      }
      const ordered = JSON.stringify(segment);
      if (orderKey.current?.segment !== ordered) orderKey.current = { segment: ordered, key: crypto.randomUUID() };
      return createOrder({ segment, sentence, idempotencyKey: orderKey.current.key });
    };
    setOrderedAt(list.priceCents);
    checkout.start(place);
  };

  const breadcrumb = (
    <p className="text-xs text-muted-foreground">
      Voter data <span aria-hidden="true">/</span> <span className="font-semibold text-foreground/80">New list</span>
    </p>
  );

  if (!v.purposeDefinition) {
    return (
      <div className="mx-auto max-w-4xl">
        {breadcrumb}
        {v.catalog.isError ? (
          <p role="alert" className="mt-10 text-sm text-destructive">
            Couldn’t load the voter data builder. Please refresh to try again.
          </p>
        ) : (
          <VoterDataPurposePicker purposes={v.purposes} value={null} onChange={v.choosePurpose} />
        )}
      </div>
    );
  }

  return (
    <MotionConfig reducedMotion="user">
      <div className="mx-auto max-w-4xl">
        {v.loading && (
          <div
            role="progressbar"
            aria-label="Updating counts"
            className="voter-loading-line fixed inset-x-0 top-0 z-[70] h-0.5 overflow-hidden"
          >
            <span className="block h-full w-[28%] rounded-full bg-primary" />
          </div>
        )}
        {listed && (
          // Zero height and sticky, so the copy rides along the top of the page column without
          // taking space; it slides in only while the real sentence is scrolled away. The app's
          // scrolling main has p-6, so -top-6 / -bottom-6 put both bars on its edges.
          <div className="sticky -top-6 z-40 h-0">
            <div
              data-testid="voter-floating-sentence"
              data-shown={sentenceAway}
              aria-hidden="true"
              className="voter-glass -mx-4 rounded-b-xl border-b transition-[transform,opacity] duration-500 ease-out data-[shown=false]:pointer-events-none data-[shown=false]:-translate-y-full data-[shown=false]:opacity-0 motion-reduce:transition-none"
            >
              <div className="flex items-center gap-4 px-4 py-2 sm:px-6">
                <p className="min-w-0 flex-1 truncate text-[15px] font-medium text-foreground/75">
                  Voters in <span className="font-semibold text-primary">{v.place}</span>
                  {v.tokenFields.map((field, i) => (
                    <Fragment key={field.id}>
                      {joinerOf(i, v.tokenFields.length)}
                      <span className="font-semibold text-primary">{phraseOf(field, rules)}</span>
                    </Fragment>
                  ))}
                  .
                </p>
                <span className="whitespace-nowrap text-sm font-bold tabular-nums">
                  <VoterEstimate estimate={list?.voters} /> {nounFor(total ?? 0, 'voter')}
                </span>
              </div>
            </div>
          </div>
        )}
        {breadcrumb}

        <div className="mt-6">
          <VoterDataPurposePicker purposes={v.purposes} value={v.purposeDefinition.purpose} onChange={v.choosePurpose} />
          {v.purposeNote && <p className="mt-2 text-sm text-muted-foreground">{v.purposeNote}</p>}
        </div>

        <section className="py-10 sm:py-14">
          <VoterListSentence list={v} headingRef={setSentence} />
        </section>

        <VoterDataListSummary
          purpose={v.purposeDefinition}
          list={segment && !counts.error ? list : null}
          pricePerRecordCents={v.catalog.data?.pricePerRecordCents}
        />

        <VoterDataSuggestions
          fields={v.fields}
          rules={rules}
          counts={segment ? counts.data : undefined}
          stale={counts.isPlaceholderData}
          capReason={v.capReason}
          onAdd={(field, values) => v.setKept(field, values)}
        />

        {segment && (
          <VoterDataExplore
            categories={v.catalog.data?.categories ?? []}
            segment={segment}
            rules={rules}
            counts={counts.data}
            provisional={v.provisional}
            capReason={v.capReason}
            onKeep={v.setKept}
            onVoted={v.setVoted}
          />
        )}

        {listed && (
          <section aria-label="Your list" className="voter-glass sticky -bottom-6 z-40 -mx-4 mt-10 rounded-t-xl border-t">
            <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-x-6 gap-y-2 px-4 pb-[max(12px,env(safe-area-inset-bottom))] pt-3 sm:px-6">
              <div>
                {list ? (
                  <p className="text-muted-foreground">
                    <span className="text-lg font-bold tabular-nums text-foreground">
                      <VoterEstimate estimate={list.voters} />
                    </span>{' '}
                    {nounFor(list.voters.value, 'voter')} <span aria-hidden="true">·</span>{' '}
                    <span className="tabular-nums">
                      <VoterEstimate estimate={list.households} />
                    </span>{' '}
                    {nounFor(list.households.value, 'household')}
                  </p>
                ) : (
                  <p className="text-muted-foreground">Counting voters…</p>
                )}
                <p className="text-xs text-muted-foreground">Live count from the voter file.</p>
              </div>
              <div className="ml-auto flex items-center gap-3">
                {list && (
                  <span className="text-lg font-semibold tabular-nums">
                    <VoterEstimate estimate={list.priceCents} dollars />
                  </span>
                )}
                {/* Nothing to click when the campaign or site can't take an order; the reason shows below. */}
                {ordering?.available !== false && (
                  <Button onClick={order} disabled={!canOrder || checkout.busy}>
                    Order
                  </Button>
                )}
              </div>
              {ordering && !ordering.available ? (
                <p className="w-full text-sm text-muted-foreground">{orderingUnavailable(ordering)}</p>
              ) : belowMinimum ? (
                <p className="w-full text-sm text-muted-foreground">The smallest order is {formatDollars(minimumOrderCents)}.</p>
              ) : (
                v.unanswered && <p className="w-full text-sm text-muted-foreground">{v.unanswered}</p>
              )}
            </div>
          </section>
        )}
        <VoterDataConfirm checkout={checkout.checkout} estimate={orderedAt} payment={checkout.payment} onClose={checkout.close} />
      </div>
    </MotionConfig>
  );
}

